import { ClientUser, UserRole } from '@numeo/types-core'
import { TeamDriverRow } from '@numeo/types-main-web-app'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { selectTeamApplicationId, useTeamDirectoryEnabled } from '@/hooks/team/useTeamDirectoryEnabled'
import type { RootState } from '@/store'
import { teamApi, useGetScopedDriverRosterQuery } from '@/store/api/teamApi'
import { listDriversFromUsers, TEAM_MAX_IDS } from '@/utils/team/teamQuery'

interface AllTeamDriversState {
    /** Every driver the caller may see, in name order. */
    rows: TeamDriverRow[]
    /** Some page has not answered yet. */
    isLoading: boolean
    /** A page could not be read: the list is not to be offered as complete. */
    isError: boolean
}

interface AllTeamDriversOptions {
    /** Before the directory serves the company, the Groups-scoped roster rather than `application.users`. */
    scoped?: boolean
    skip?: boolean
}

const NO_ROWS: TeamDriverRow[] = []
const NO_USERS: ClientUser[] = []

/**
 * Every driver of the company the caller may see, for the pickers that search their own option list (ds2
 * `Autocomplete` searches the options it holds): `GET /team/drivers/v1` 200 at a time, page after page until the
 * total is covered, each page cached five minutes like any list. Screens that page and search on the server use
 * `useTeamDrivers` instead.
 */
export const useAllTeamDrivers = (options: AllTeamDriversOptions = {}): AllTeamDriversState => {
    const dispatch = useAppDispatch()
    const enabled = useTeamDirectoryEnabled()
    const applicationId = useAppSelector(selectTeamApplicationId)
    const skip = !!options.skip
    const scopedRoster = !enabled && !!options.scoped && !skip

    const roster = useGetScopedDriverRosterQuery(scopedRoster && applicationId ? { applicationId } : skipToken)
    const users = useAppSelector((state) => (enabled || scopedRoster ? undefined : state.auth.application?.users))

    // Page 1 says how many there are; the rest are asked for together once it has.
    const firstArg = enabled && applicationId && !skip ? { applicationId, page: 1, limit: TEAM_MAX_IDS } : undefined
    const first = useAppSelector((state: RootState) => (firstArg ? teamApi.endpoints.listTeamDrivers.select(firstArg)(state) : undefined))
    const total = first?.data?.pagination.total ?? 0
    const pageCount = Math.max(1, Math.ceil(total / TEAM_MAX_IDS))
    const args = useMemo(
        () =>
            enabled && applicationId && !skip ? Array.from({ length: pageCount }, (_, index) => ({ applicationId, page: index + 1, limit: TEAM_MAX_IDS })) : [],
        [enabled, applicationId, skip, pageCount]
    )

    useEffect(() => {
        const subscriptions = args.map((arg) => dispatch(teamApi.endpoints.listTeamDrivers.initiate(arg)))
        return () => subscriptions.forEach((subscription) => subscription.unsubscribe())
    }, [dispatch, args])

    const selectors = useMemo(() => args.map((arg) => teamApi.endpoints.listTeamDrivers.select(arg)), [args])
    const pages = useAppSelector((state: RootState) => selectors.map((select) => select(state)), shallowEqual)

    return useMemo((): AllTeamDriversState => {
        if (skip) return { rows: NO_ROWS, isLoading: false, isError: false }
        if (!enabled) {
            const source = scopedRoster ? (roster.data ?? NO_USERS) : (users ?? NO_USERS)
            const rows = source.filter((user) => user.role === UserRole.DRIVER)
            return {
                rows: rows.length ? listDriversFromUsers(rows, { page: 1, limit: rows.length }).items : NO_ROWS,
                isLoading: scopedRoster && roster.isLoading,
                isError: scopedRoster && roster.isError,
            }
        }
        const rows = pages.flatMap((page) => page.data?.items ?? [])
        return { rows, isLoading: pages.some((page) => !page.data && !page.isError), isError: pages.some((page) => page.isError) }
    }, [skip, enabled, scopedRoster, roster.data, roster.isLoading, roster.isError, users, pages])
}
